// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/UpdateChangeRequestOrder";
import { OrderChangeRequestNewLineModel } from "@store/Framework/Types/ApiModels";
import { createHandlerChainRunner, Handler } from "@toolkit/Modules/client-framework/HandlerCreator";

interface Parameter {
    productId: string;
    unitOfMeasure: string;
    qtyOrdered: number;
}

interface Props {
    pricing?: ProductInfo["pricing"];
}

type HandlerType = Handler<Parameter, Props>;

// Fetch real-time pricing for a single product and capture the result. Mirrors how the base
// ProductSelector SetProduct chain loads real-time inventory (dispatch + waitFor gate).
export const RequestPricing: HandlerType = async props => {
    const { productId, unitOfMeasure, qtyOrdered } = props.parameter;
    let completed = false;

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [{ productId, unitOfMeasure, qtyOrdered }],
            onComplete: realTimePricingProps => {
                if (!realTimePricingProps.error) {
                    props.pricing = realTimePricingProps.apiResult?.realTimePricingResults?.find(
                        o => o.productId === productId,
                    );
                }
                completed = true;
            },
        }),
    );

    await waitFor(() => completed);
};

// Write the fetched pricing onto the matching new line. Read the current state here (rather than
// closing over a snapshot) so concurrent edits to other new lines are not clobbered.
export const ApplyPricingToNewLine: HandlerType = props => {
    const { productId, qtyOrdered } = props.parameter;
    const newLines = props.getState().greif.pages.orderChange.changeRequestOrder?.newLines;
    const line = newLines?.find(x => x.product.id === productId);

    // The quantity is updated in state synchronously before this fetch is dispatched, so if it no
    // longer matches the quantity this request was made for, a newer request is in flight (or has
    // already applied) for this line - skip so this stale response can't overwrite it.
    // Also skip if the request produced no pricing (error/deferred/no-permission) - there's nothing
    // to apply, and dispatching anyway would only create new object references for an unchanged value.
    if (!newLines || !line || Number(line.newQuantity) !== qtyOrdered || props.pricing === undefined) {
        return;
    }

    const updatedNewLines = newLines.map<OrderChangeRequestNewLineModel>(x =>
        x.product.id === productId
            ? {
                  ...x,
                  productInfo: {
                      ...x.productInfo,
                      pricing: props.pricing,
                  },
              }
            : x,
    );

    props.dispatch(updateChangeRequestOrder({ newLines: updatedNewLines }));
};

export const chain = [RequestPricing, ApplyPricingToNewLine];

const loadNewLinePricing = createHandlerChainRunner(chain, "LoadNewLinePricing");
export default loadNewLinePricing;
